import "/node_modules/flag-icons/css/flag-icons.min.css";
import { useMemo } from "react";
import { Language } from "../api/languageApi.ts";
import { NavLink } from "react-router";
import { useLanguageStore } from "../stores/languageStore.ts";
import {
  getTranslation,
  useSupportedInstruments,
} from "../api/instrumentApi.ts";
import { SearchResultDto } from "../api/teacherSearchApi.ts";
import { Trans } from "@lingui/react/macro";
import { ProfilePicture } from "./profile/ProfilePicture.tsx";
import { useQueryUrl } from "../hooks/useQueryNav.ts";

interface UserCardProps {
  user: SearchResultDto;
  supportedLanguages?: Language[];
}

const UserCard = ({ user, supportedLanguages }: UserCardProps) => {
  const languages = useMemo(
    () =>
      supportedLanguages?.filter((language) =>
        user.languages.includes(language.locale),
      ),
    [user, supportedLanguages],
  );

  const { language } = useLanguageStore();
  const { data: supportedInstruments } = useSupportedInstruments();

  function buildUserInstruments() {
    return user.instruments.reduce(
      (accumulator: undefined | string, currentValue) => {
        const instrument = supportedInstruments.find(
          (instrument) => instrument.name === currentValue,
        );
        const displayName = instrument
          ? getTranslation(instrument, language)
          : "";

        if (!accumulator) {
          return displayName;
        }
        return accumulator + " / " + displayName;
      },
      undefined,
    );
  }

  const buildUrl = useQueryUrl();

  return (
    <NavLink
      to={buildUrl(`/profile/${user.id}`, { locationId: user.locationId })}
      className="border-soluud-border-subtle bg-soluud-background-card relative flex flex-col items-center rounded-xl border p-6 pb-20 shadow-[--shadow-card] transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[--shadow-card-hover]"
    >
      {languages && languages.length > 0 && (
        <div className="absolute top-2 right-2 flex flex-col space-y-1">
          {languages.map((lang) => (
            <span
              key={lang.locale}
              className={`fi fi-${lang.country}`}
              title={lang.displayText}
            ></span>
          ))}
        </div>
      )}

      <div className="mb-2 h-40 w-40 overflow-hidden rounded-full">
        <ProfilePicture
          profilePictureUrl={user.profilePictureUrl}
          firstName={user.firstName}
          lastName={user.lastName}
        ></ProfilePicture>
      </div>

      <p className="text-lg font-semibold text-gray-800">
        {user.firstName} {user.lastName}
      </p>
      <p className={"text-xs font-light"}>{buildUserInstruments()}</p>

      {user.distanceInMeters && (
        <div className="absolute right-2 bottom-2 text-xs text-gray-500">
          <Trans>approx.</Trans> {(user.distanceInMeters / 1000).toFixed(1)}km
        </div>
      )}
    </NavLink>
  );
};

export default UserCard;
